(() => {
  'use strict';
  if (window.klikDiscoveryInstalled) return;
  window.klikDiscoveryInstalled = true;
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u064B-\u065F\u0670\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  const allowedSorts = ['featured','title-asc','title-desc','price-asc','price-desc'];
  const installs = new WeakMap();

  function installCategorySwipe(nav) {
    if (!window.PointerEvent || nav.dataset.kdSwipeReady) return;
    let gesture = null, suppressClickUntil = 0;
    const reset = () => {
      const previous = gesture;
      gesture = null;
      nav.classList.remove('is-dragging');
      if (previous && nav.hasPointerCapture?.(previous.id)) nav.releasePointerCapture(previous.id);
      return previous;
    };
    nav.addEventListener('pointerdown', event => {
      suppressClickUntil = 0;
      // Touch and pen scrolling belong to the browser, including diagonal swipes.
      if (event.pointerType !== 'mouse') { reset(); return; }
      if (!event.isPrimary || event.button !== 0) { reset(); return; }
      if (nav.scrollWidth <= nav.clientWidth + 1) return;
      // Mouse dragging stops any existing programmatic scroll.
      nav.scrollTo({left:nav.scrollLeft,behavior:'auto'});
      gesture = {id:event.pointerId,x:event.clientX,y:event.clientY,left:nav.scrollLeft,moved:false};
    }, {passive:true});
    nav.addEventListener('pointermove', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (!gesture.moved) {
        if (Math.max(Math.abs(dx),Math.abs(dy)) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) { reset(); return; }
        gesture.moved = true;
        nav.classList.add('is-dragging');
        try { nav.setPointerCapture(event.pointerId); } catch (_) { /* Native scrolling remains available without capture. */ }
      }
      if (event.cancelable) event.preventDefault();
      // Native scrollLeft is negative in RTL; the physical drag delta works in both directions.
      nav.scrollLeft = gesture.left - dx;
    }, {passive:false});
    nav.addEventListener('pointerup', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const previous = reset();
      if (!previous.moved) return;
      suppressClickUntil = performance.now() + 400;

    });
    const cancel = event => { if (gesture?.id === event.pointerId) reset(); };
    nav.addEventListener('pointercancel',cancel);
    // Only losing this row's mouse capture cancels its drag.
    nav.addEventListener('lostpointercapture', event => {
      if (event.target === nav) cancel(event);
    });
    nav.addEventListener('click', event => {
      if (event.detail > 0 && performance.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
      suppressClickUntil = 0;
    },true);
    nav.addEventListener('dragstart',event => event.preventDefault());
    nav.querySelectorAll('a,img').forEach(element => { element.draggable = false; });
    nav.dataset.kdSwipeReady = 'true';
  }

  const preparing = new WeakSet();
  async function prepare(browser) {
    if (preparing.has(browser) || installs.has(browser)) return;
    preparing.add(browser);
    const total = Number(browser.dataset.kdCatalogPages || 1);
    const current = Number(browser.dataset.kdCatalogPage || 1);
    const grid = browser.querySelector('[data-kd-grid]');
    if (total > 1 && total <= 10 && grid && browser.dataset.kdSection) {
      browser.dataset.kdHydrating = 'true';
      const ar = browser.dataset.kdLanguage === 'ar';
      const count = browser.querySelector('[data-kd-count]');
      if (count) count.textContent = ar ? 'جارٍ تحميل كل التصاميم…' : 'Loading all designs…';
      const known = new Set(Array.from(grid.querySelectorAll('[data-product-handle]'), card => card.dataset.productHandle));
      try {
        const additions = [];
        for (let page = 1; page <= total; page++) {
          if (page === current) continue;
          const url = new URL(location.href);
          url.searchParams.set('section_id', browser.dataset.kdSection);
          url.searchParams.set('page', String(page));
          const response = await fetch(url, {signal: AbortSignal.timeout(15000)});
          if (!response.ok) throw new Error('Catalog page unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = doc.querySelector('[data-kd-grid]');
          if (!source) throw new Error('Catalog page incomplete');
          for (const card of source.querySelectorAll('[data-kd-card]')) {
            if (!known.has(card.dataset.productHandle)) {
              known.add(card.dataset.productHandle); additions.push(card);
            }
          }
        }
        const fragment = document.createDocumentFragment();
        for (const card of additions) fragment.append(document.importNode(card, true));
        grid.append(fragment);
        browser.dataset.kdPageScope = 'all';
        browser.dataset.kdCatalogComplete = 'true';
        const sourcePages = browser.querySelector('[data-kd-source-pages]');
        if (sourcePages) sourcePages.hidden = true;
      } catch (_) {
        browser.dataset.kdCatalogComplete = 'false';
      }
      delete browser.dataset.kdHydrating;
    }
    preparing.delete(browser);
    install(browser);
  }

  function install(browser) {
    if (installs.has(browser)) return;
    const grid = browser.querySelector('[data-kd-grid]');
    if (!grid) return;
    const ar = location.hostname.startsWith('ar.') || document.documentElement.lang.toLowerCase().startsWith('ar') || browser.dataset.kdLanguage === 'ar';
    const partial = browser.dataset.kdPageScope === 'partial';
    const pageSize = Math.max(1, Number(browser.dataset.kdPageSize) || 24);
    const count = browser.querySelector('[data-kd-count]');
    const query = browser.querySelector('[data-kd-query]');
    const mode = browser.querySelector('[data-kd-mode-filter]');
    const availability = browser.querySelector('[data-kd-availability]');
    const sort = browser.querySelector('[data-kd-sort]');
    const priceBasis = browser.querySelector('[data-kd-price-basis]');
    const priceBasisField = priceBasis?.closest('label');
    const budget = browser.querySelector('[data-ko-filter="budget"]');
    const priceNote = browser.querySelector('[data-kb-comparison-note]');
    const extra=Array.from(browser.querySelectorAll('[data-ko-filter]'));
    const extraState=()=>Object.fromEntries(extra.map(n=>[n.dataset.koFilter,n.value]));
    const pager = browser.querySelector('[data-kd-pagination]');
    const empty = browser.querySelector('[data-kd-empty]');
    const controls = browser.querySelector('[data-kd-controls]');
    const controlFields = Array.from(controls?.querySelectorAll('.kd-field') || []);
    const filterPanel = browser.querySelector('[data-kd-filter-panel]');
    const filterCount = browser.querySelector('[data-kd-filter-count]');
    const smallScreen = matchMedia('(max-width:749px)');
    const activeFilters = () => [query?.value.trim(), priceBasis?.value, mode?.value !== 'all' && mode?.value, availability?.value !== 'all' && availability?.value, sort?.value !== 'featured' && sort?.value, ...extra.map(n => n.value)].filter(Boolean).length;
    const layoutFilters = () => { if (filterPanel) filterPanel.open = browser.dataset.kdCompactFilters === 'true' ? activeFilters() > 0 : !smallScreen.matches || activeFilters() > 0; };
    smallScreen.addEventListener('change', layoutFilters);
    const modeField = mode?.closest('label'), availabilityField = availability?.closest('label');
    const sourceLinks = Array.from(browser.querySelectorAll('[data-kd-source-pages] a[href]'));
    const priceOptions = Array.from(browser.querySelectorAll('[data-kd-price-option]'));
    const labels = ar ? {designs:'تصميم',of:'من',previous:'السابق',next:'التالي',page:'صفحة',partial:'في هذه المجموعة من النتائج'} : {designs:'designs',of:'of',previous:'Previous',next:'Next',page:'Page',partial:'in this set'};
    const collator = new Intl.Collator(ar ? 'ar' : 'en', {numeric:true,sensitivity:'base'});
    const titleEntries = new Map();
    const entries = Array.from(grid.children).filter(card => {if(!card.matches('[data-kd-card]'))return false;const quote=!!card.querySelector('a[href*="/products/fully-custom-order"]');if(quote)card.hidden=true;return !quote;}).map((card,index) => {
      const title = card.querySelector('.grid-product__title');
      const entry = {card,index,title,mode:card.dataset.kdMode,available:card.dataset.kdAvailable === 'true',price:Number(card.dataset.kdPrice)};
      entry.facts=window.KlikOptimal?.readCard(card);
      // The browsing card supplies its displayed reference total. Native minimum
      // variant prices can be zero or generated options and are not a budget basis.
      const priceMarker=card.querySelector('.kb-price [data-kb-comparison-price]');
      entry.priceMarker=priceMarker;
      entry.priceBasis=priceMarker?.dataset.kbPriceBasis||'';
      entry.priceQuantity=Number(card.querySelector('[data-kb-example]')?.dataset.kbExample)||0;
      if(priceMarker)entry.price=Number(priceMarker.dataset.kbComparisonPrice);
      else if(entry.mode==='ready'&&!card.querySelector('.grid-product__price [data-klik-price-unit], .grid-product__price .product__unit-price'))entry.priceBasis='ready';
      entry.priceComparable=!!entry.priceBasis&&Number.isFinite(entry.price)&&entry.price>0;
      card.querySelectorAll('a[href*="/products/"]').forEach(link=>{
        const u=new URL(link.href,location.origin);
        if(entry.priceBasis&&entry.priceBasis!=='ready'){
          u.searchParams.set('klik_price_basis',entry.priceBasis);
          if(entry.priceQuantity)u.searchParams.set('klik_price_quantity',String(entry.priceQuantity));
          link.href=u.href;
        }
      });
      entry.name = title?.textContent || card.dataset.kdTitle || '';
      entry.search = normalize(entry.name + ' ' + (card.dataset.kdTitle || ''));
      if (title) titleEntries.set(title,entry);
      return entry;
    });
    if(grid.querySelector('a[href*="/products/fully-custom-order"]')){
      const quote=document.createElement('p');quote.className='ko-notice';
      const link=document.createElement('a');link.href=window.KlikOptimal?.url('/products/fully-custom-order')||'/products/fully-custom-order';link.textContent=ar?'عندك تصميم آخر؟ أرسل فكرتك لعرض سعر.':'Have another design in mind? Request a custom quote.';quote.append(link);grid.before(quote);
    }
    const priceBases=new Set(entries.map(entry=>entry.priceBasis).filter(Boolean));
    if(priceBasis){
      Array.from(priceBasis.options).forEach(option=>{if(option.value){option.hidden=!priceBases.has(option.value);option.disabled=option.hidden;}});
      if(priceBasisField)priceBasisField.hidden=priceBases.size===1&&entries.every(entry=>entry.priceComparable);
    }
    const modes = new Set(entries.map(entry => entry.mode));
    const availabilityStates = new Set(entries.map(entry => entry.available));
    let ordered = entries.slice(), matches = entries, page = 1, lastOrder = 'featured', lastSignature = '', pagerSignature = '', timer = null, composing = false;
    const visibility = new Map(entries.map(entry => [entry, !entry.card.hidden]));
    function refreshPrices(){
      let changed=false;
      entries.forEach(entry=>{const next=Number(entry.priceMarker?.dataset.kbComparisonPrice);if(next>0&&next!==entry.price){entry.price=next;changed=true;}});
      if(changed){lastOrder='';lastSignature='';apply(false,true);}
    }
    document.addEventListener('klik:prices:updated',refreshPrices);
    count?.setAttribute('data-wg-notranslate','');
    pager?.setAttribute('data-wg-notranslate','');
    browser.dataset.kdReady = 'true';
    if (controls) controls.hidden = entries.length === 0;
    if (filterPanel) filterPanel.hidden = entries.length === 0;

    function readState() {
      const params = new URL(location.href).searchParams;
      extra.forEach(n=>{n.value=params.get('klik_'+n.dataset.koFilter)||''});
      if(extra.some(n=>n.value))browser.querySelector('[data-ko-advanced]').open=true;
      if(priceBasis){
        priceBasis.value=priceBases.has(params.get('klik_price_type'))?params.get('klik_price_type'):'';
        // Earlier budget bookmarks explicitly covered only whole ready-made items.
        if(params.get('klik_budget')&&!params.has('klik_price_type')&&priceBases.has('ready'))priceBasis.value='ready';
        if(priceBasisField&&priceBasis.value)priceBasisField.hidden=false;
      }
      if (query) query.value = params.get('klik_filter') || '';
      if (mode) mode.value = ['ready','custom'].includes(params.get('klik_mode')) ? params.get('klik_mode') : 'all';
      if (availability) availability.value = ['available','unavailable'].includes(params.get('klik_available')) ? params.get('klik_available') : 'all';
      if (sort) sort.value = allowedSorts.includes(params.get('klik_sort')) ? params.get('klik_sort') : 'featured';
      page = Math.max(1,parseInt(params.get('klik_page'),10) || 1);
    }
    function statePairs() {
      return [['klik_price_type',priceBasis?.value||'',''],...extra.map(n=>['klik_'+n.dataset.koFilter,n.value,'']),['klik_filter',query?.value.trim() || '',''],['klik_mode',mode?.value || 'all','all'],['klik_available',availability?.value || 'all','all'],['klik_sort',sort?.value || 'featured','featured']];
    }
    function retainState() {
      const u = new URL(location.href);
      for (const [key,value,fallback] of [...statePairs(),['klik_page',String(page),'1']]) {
        if (value === fallback) u.searchParams.delete(key); else u.searchParams.set(key,value);
      }
      if (u.href !== location.href) history.replaceState(history.state,'',u.href);
      sourceLinks.forEach(link => {
        const next = new URL(link.href,location.href);
        // Carry search intent and review context without overwriting native page/view parameters.
        for (const key of ['q','type','options[prefix]','klik_query','preview_theme_id','klik_design']) {
          if (u.searchParams.has(key)) next.searchParams.set(key,u.searchParams.get(key));
        }
        for (const [key,value,fallback] of statePairs()) {
          if (value === fallback) next.searchParams.delete(key); else next.searchParams.set(key,value);
        }
        next.searchParams.delete('klik_page');
        if (link.href !== next.href) link.href = next.href;
      });
    }
    function button(label,target,current=false) {
      const el=document.createElement('button');el.type='button';el.textContent=label;
      el.setAttribute('aria-label',/^\d+$/.test(label)?`${labels.page} ${label}`:label);
      if(current)el.setAttribute('aria-current','page');
      el.addEventListener('click',()=>{if(timer!==null){cancelInput();apply();}page=target;render(true);matches[(page-1)*pageSize]?.card.querySelector('a')?.focus({preventScroll:true});});
      return el;
    }
    const applied=document.createElement('div');applied.className='kd-applied';
    applied.setAttribute('role','group');applied.setAttribute('aria-label',ar?'الفلاتر المختارة':'Applied filters');
    applied.hidden=true;browser.querySelector('.kd-toolbar')?.after(applied);
    function renderApplied(){
      const fields=[query,mode,availability,priceBasis,...extra,sort].filter(Boolean);
      const defaults=new Map([[mode,'all'],[availability,'all'],[sort,'featured']]);
      const selected=fields.filter(field=>field.value&&field.value!==(defaults.get(field)||''));
      const sig=selected.map(f=>fields.indexOf(f)+':'+f.value).join('|');
      if(applied.dataset.signature===sig)return;
      applied.dataset.signature=sig;applied.hidden=!selected.length;applied.replaceChildren();
      for(const field of selected){
        const rawLabel=field.closest('label')?.childNodes;
        const label=rawLabel?[...rawLabel].filter(n=>n.nodeType===3||n.nodeType===1&&n.tagName==='SPAN').map(n=>n.textContent.trim()).filter(Boolean).join(' '):'';
        const value=field.tagName==='SELECT'?field.selectedOptions[0]?.textContent:field.value;
        const b=document.createElement('button');b.type='button';b.className='kd-filter-chip';
        const name=(label?label+': ':'')+value;b.textContent=name;
        const cross=document.createElement('span');cross.textContent='×';cross.setAttribute('aria-hidden','true');b.append(cross);
        b.setAttribute('aria-label',(ar?'إزالة الفلتر: ':'Remove filter: ')+name);
        b.addEventListener('click',()=>{field.value=defaults.get(field)||'';cancelInput();apply();(applied.querySelector('button')||filterPanel?.querySelector('summary')||query)?.focus({preventScroll:true});});
        applied.append(b);
      }
      if(selected.length>1){const clear=document.createElement('button');clear.type='button';clear.className='kd-clear-applied';clear.textContent=ar?'مسح الكل':'Clear all';clear.addEventListener('click',()=>{browser.querySelector('[data-kd-reset]')?.click();filterPanel?.querySelector('summary')?.focus({preventScroll:true});});applied.append(clear);}
    }
    function render(scroll) {
      const maxPage=Math.max(1,Math.ceil(matches.length/pageSize));
      page=Math.min(maxPage,Math.max(1,page));
      const visible=new Set(matches.slice((page-1)*pageSize,page*pageSize));
      entries.forEach(entry=>{const next=visible.has(entry);if(visibility.get(entry)!==next){entry.card.hidden=!next;visibility.set(entry,next);}});
      if(empty)empty.hidden=matches.length>0;
      if(count){const range=matches.length?`${(page-1)*pageSize+1}–${Math.min(page*pageSize,matches.length)} ${labels.of} `:'';const text=`${range}${matches.length} ${labels.designs}${partial?` ${labels.partial}`:''}`;if(count.textContent!==text)count.textContent=text;}
      const nextPager=`${page}/${maxPage}`;
      if(pager && nextPager!==pagerSignature){
        pagerSignature=nextPager;pager.replaceChildren();pager.hidden=maxPage<=1;
        if(maxPage>1){
          const previous=button(labels.previous,page-1);previous.disabled=page===1;pager.append(previous);
          const start=Math.max(1,Math.min(page-1,maxPage-2)),end=Math.min(maxPage,start+2);
          for(let n=start;n<=end;n++)pager.append(button(String(n),n,n===page));
          const position=document.createElement('span');position.className='kd-pagination__position';position.textContent=`${page} / ${maxPage}`;pager.append(position);
          const next=button(labels.next,page+1);next.disabled=page===maxPage;pager.append(next);
        }
      }
      retainState();renderApplied();
      if(scroll)browser.scrollIntoView({block:'start',behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
    }
    function apply(resetPage=true,force=false) {
      const term=normalize(query?.value),alias=normalize(window.klikSearchQuery?window.klikSearchQuery(query?.value||''):term);
      const requestedMode=mode?.value||'all',requestedAvailability=availability?.value||'all';
      let order=sort?.value||'featured';
      const signatureFor=()=>[term,alias,requestedMode,requestedAvailability,priceBasis?.value||'',order,JSON.stringify(extraState())].join('\u0001');
      if(signatureFor()===lastSignature&&!force){retainState();return;}
      const filters=extraState();
      delete filters.budget;
      const hasOtherFilters=Object.values(filters).some(Boolean);
      const baseSelected=entries.filter(entry=>(!hasOtherFilters||(entry.facts&&window.KlikOptimal.matches(entry.facts,filters)))&&(!priceBasis?.value||entry.priceBasis===priceBasis.value)&&(!term||entry.search.includes(term)||(alias&&entry.search.includes(alias)))&&(requestedMode==='all'||entry.mode===requestedMode)&&(requestedAvailability==='all'||entry.available===(requestedAvailability==='available')));
      const bases=new Set(baseSelected.map(entry=>entry.priceBasis==='reference'?`reference:${entry.priceQuantity}`:entry.priceBasis));
      const canPrice=baseSelected.length>0&&bases.size===1&&baseSelected.every(entry=>entry.priceComparable);
      const basis=canPrice?baseSelected[0].priceBasis:'';
      const basisLabel={seat:['Set of 7 estimate','تقدير طقم 7 مقاعد'],meter:['5 m estimate','تقدير 5 أمتار'],piece:['Starting price per piece','سعر البداية للقطعة'],ready:['Complete price','السعر الكامل'],reference:['Reference-size estimate','تقدير المقاس المرجعي']}[basis]||['Price','السعر'];
      priceOptions.forEach(option=>{
        const desc=option.value==='price-desc';
        const en=basisLabel[0]+(desc?': high to low':': low to high');
        const arabic=basisLabel[1]+(desc?' · الأعلى أولًا':' · الأقل أولًا');
        option.dataset.klikI18nEn=en;option.dataset.klikI18nAr=arabic;
        const text=ar?arabic:en;if(option.textContent!==text)option.textContent=text;
        option.disabled=!canPrice;option.hidden=!canPrice;
      });
      if(budget){budget.disabled=!canPrice;if(!canPrice)budget.value='';}
      if(priceNote){
        const note=canPrice?(basis==='ready'?(ar?'المقارنة حسب السعر الكامل للتشكيل الموضح.':'Comparing complete prices for the displayed configurations.'):(basis==='piece'?(ar?'المقارنة حسب سعر البداية للقطعة؛ الخيارات قد تغيّر الإجمالي.':'Comparing starting prices per piece; options can change the total.'):(ar?'الترتيب والميزانية حسب الإجمالي التقديري الموضح؛ الخيارات قد تغيّر الإجمالي.':'Sorting and budget use the displayed estimate; options can change the total.'))):(ar?'اختر نوعًا واحدًا من الأسعار لتفعيل الترتيب بالسعر والميزانية.':'Choose one price type to sort by price and compare budgets.');
        if(priceNote.textContent!==note)priceNote.textContent=note;
      }
      const limit=Number(budget?.value)||0;
      const selected=baseSelected.filter(entry=>!limit||(entry.priceComparable&&entry.price<=limit*100));
      if(sort?.value.startsWith('price-')&&!canPrice)sort.value='featured';
      order=sort?.value||'featured';
      if(modeField){const hidden=!partial&&modes.size<2&&requestedMode==='all';if(modeField.hidden!==hidden)modeField.hidden=hidden;}
      if(availabilityField){const hidden=!partial&&availabilityStates.size<2&&requestedAvailability==='all';if(availabilityField.hidden!==hidden)availabilityField.hidden=hidden;}
      if(controls){const fields=String(controlFields.filter(field=>!field.hidden).length);if(controls.dataset.kdFields!==fields)controls.dataset.kdFields=fields;}
      if(filterCount){const active=activeFilters();filterCount.hidden=!active;const label=active?(ar?`${active} محدد`:`${active} active`):'';if(filterCount.textContent!==label)filterCount.textContent=label;}
      const signature=signatureFor();
      if(signature===lastSignature&&!force){retainState();return;}
      lastSignature=signature;
      // Query/filter changes never move card nodes. Sort once only when the order changes
      // or translated titles invalidate a currently selected name order.
      if(order!==lastOrder||(force&&order.startsWith('title-'))){
        ordered=entries.slice();
        ordered.sort((a,b)=>{
          if(order.startsWith('title-'))return (order==='title-desc'?-1:1)*collator.compare(a.name,b.name)||a.index-b.index;
          if(order.startsWith('price-')){if(a.priceComparable!==b.priceComparable)return a.priceComparable?-1:1;return (order==='price-desc'?-1:1)*(a.price-b.price)||a.index-b.index;}
          return a.index-b.index;
        });
        const current=Array.from(grid.children);
        if(ordered.some((entry,index)=>current[index]!==entry.card)){const fragment=document.createDocumentFragment();ordered.forEach(entry=>fragment.append(entry.card));grid.append(fragment);}
        lastOrder=order;
      }
      const selectedSet=new Set(selected);matches=ordered.filter(entry=>selectedSet.has(entry));
      if(resetPage)page=1;
      render(false);
    }
    function cancelInput(){if(timer!==null){clearTimeout(timer);timer=null;}}
    function scheduleInput(){cancelInput();if(composing)return;timer=setTimeout(()=>{timer=null;apply();},80);}
    function immediate(){cancelInput();apply();}
    query?.addEventListener('input',event=>{if(!event.isComposing)scheduleInput();});
    query?.addEventListener('compositionstart',()=>{composing=true;cancelInput();});
    query?.addEventListener('compositionend',()=>{composing=false;scheduleInput();});
    extra.forEach(n=>{n.addEventListener('change',()=>{immediate();window.KlikOptimal?.track('klik_catalog_filtered',{result_count:matches.length})});if(n.type==='number')n.addEventListener('input',scheduleInput)});
    priceBasis?.addEventListener('change',immediate);mode?.addEventListener('change',immediate);availability?.addEventListener('change',immediate);sort?.addEventListener('change',immediate);
    browser.querySelector('[data-kd-reset]')?.addEventListener('click',()=>{cancelInput();if(query)query.value='';if(mode)mode.value='all';if(availability)availability.value='all';if(sort)sort.value='featured';if(priceBasis)priceBasis.value='';extra.forEach(n=>n.value='');apply();});
    const popstate=()=>{cancelInput();readState();apply(false,true);layoutFilters();};window.addEventListener('popstate',popstate);
    // Weglot can replace visible titles after this script. Refresh only affected cached
    // entries; money/Saved mutations and ordinary visibility changes do not rescan cards.
    const observer=new MutationObserver(records=>{
      let changed=false;
      const affected=new Set();
      records.forEach(record=>{const target=record.target.nodeType===1?record.target:record.target.parentElement;const entry=titleEntries.get(target?.closest('.grid-product__title'));if(entry)affected.add(entry);});
      affected.forEach(entry=>{const next=entry.title.textContent;if(next!==entry.name){entry.name=next;entry.search=normalize(next+' '+(entry.card.dataset.kdTitle||''));changed=true;}});
      if(changed){lastSignature='';apply(false,true);}
    });
    observer.observe(grid,{childList:true,characterData:true,subtree:true});
    installs.set(browser,()=>{smallScreen.removeEventListener('change',layoutFilters);cancelInput();observer.disconnect();document.removeEventListener('klik:prices:updated',refreshPrices);window.removeEventListener('popstate',popstate);});
    readState();apply(false,true);layoutFilters();
  }
  function init(scope=document){scope.querySelectorAll('.kd-category-nav').forEach(installCategorySwipe);scope.querySelectorAll('[data-kd-browser]').forEach(prepare);}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(),{once:true});else init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('[data-kd-browser]').forEach(browser=>installs.get(browser)?.()));
})();
